import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";

import useAppStore from "@/store/use-app-store";
import { Button } from "@mui/material";
import {
  consoleBeauty,
  dateToString,
  isIdentical,
  isObjectEmpty,
  isOnline,
  isValidToken,
} from "data/functions";
import { handleLogout } from "pages/settings/ExitButton";
import { v4 as uuidv4 } from "uuid";
import useAlertStore from "./store/use-alert-store";

function TokenUpdater() {
  const auth = useAuth();
  const { token } = useAppStore();
  const [needLogout, setNeedLogout] = useState(false);
  const [errCount, setErrCount] = useState(0);

  const updateTokenData = (user) => {
    if (isObjectEmpty(user)) return;
    const { access_token, expires_at } = user || {};
    const profile = user?.profile || {};

    if (!expires_at) return;

    const tokenExpiresAt = new Date(expires_at * 1000).getTime();
    if (
      !isIdentical(useAppStore.getState()?.token, access_token) &&
      (!useAppStore.getState()?.tokenExpiresAt ||
        useAppStore.getState()?.tokenExpiresAt < tokenExpiresAt)
    ) {
      setNeedLogout(false);
      consoleBeauty(
        "Токен установлен до:\n" + new Date(tokenExpiresAt).toString(),
        "KeyCloak",
        "dimgrey",
      );

      useAppStore.setState({
        token: access_token,
        tokenExpiresAt,
      });
    }

    let companyParameters = {
      ...(isIdentical(useAppStore.getState()?.companyUuid, profile.company_uuid)
        ? {}
        : { companyUuid: profile.company_uuid }),
      ...(isIdentical(useAppStore.getState()?.userUuid, profile.user_uuid)
        ? {}
        : { userUuid: profile.user_uuid }),
      ...(isIdentical(useAppStore.getState()?.clientName, profile.name)
        ? {}
        : { clientName: profile.name }),
    };

    if (!isObjectEmpty(companyParameters)) {
      useAppStore.setState(companyParameters);
    }
  };
  // // 🔄 Обновление токена через silent renew
  const refreshToken = async () => {
    try {
      consoleBeauty("Попытка обновления токена", "KeyCloak", "dimgrey");

      const user = await auth.signinSilent();
      // console.log(user);

      if (user) {
        consoleBeauty("Токен успешно обновлён", "KeyCloak", "dimgrey");
        updateTokenData(user);
        setErrCount(0);
      } else {
        localStorage.clear();
        handleLogout(auth);
        setErrCount((e) => e + 1);
      }
      return { success: true };
    } catch (error) {
      if (errCount > 5) {
        localStorage.clear();
        handleLogout(auth);
        // window.location.reload(false);
      }
      setErrCount((e) => e + 1);
      consoleBeauty("Не удалось обновить токен\n" + error, "KeyCloak", "red");
    }
    return { success: false };
  };

  // 🔁 Подписка на события обновления пользователя (без этого не обновляется user)
  useEffect(() => {
    if (!auth.events) return;

    const handleUserLoaded = (user) => {
      consoleBeauty("Смена пользователя..", "KeyCloak", "dimgrey");
      updateTokenData(user);
    };

    auth.events.addUserLoaded(handleUserLoaded);
    return () => {
      auth.events.removeUserLoaded(handleUserLoaded);
    };
  }, [auth.events]);

  const urlParameters = new URLSearchParams(window.location.search);
  const error = urlParameters.get("error");

  const [triggerAuth, setTriggerAuth] = useState(false);
  const lastExecutedReference = useRef(0);
  const [pause, setPause] = useState(true);
  useEffect(() => {
    if (!pause) return;

    setTimeout(() => {
      setPause((e) => {
        if (e === true) return false;
        return e;
      });
    }, 2000);
  }, [pause]);

  useEffect(() => {
    if (pause) return;
    setPause(true);

    if (needLogout) {
      handleLogout(auth);
      return;
    }

    authAlert({ authError: auth?.error, error, triggerAuth });
    return () => {
      // useAlertStore.setState({
      //   alertArray: [],
      // });
    };
  }, [error, auth?.error, triggerAuth]);

  async function authAlert(params = {}) {
    const { authError, error } = params;

    if (authError || error) {
      setErrCount((e) => e + 1);
      if (errCount > 15) {
        setNeedLogout(true);
        setErrCount(0);
        return;
      }
      const online = await isOnline();
      if (online) {
        if (!isObjectEmpty(useAppStore.getState()?.message)) {
          useAppStore.setState({
            cacheMessage: useAppStore.getState()?.message,
          });
        }
        auth.signinRedirect();
      } else {
        useAlertStore.setState({
          alertArray: [
            {
              button: (
                <Button
                  color="inherit"
                  onClick={() => {
                    auth && handleLogout(auth);
                  }}
                  size="small"
                >
                  Выйти
                </Button>
              ),
              id: "system-alert",
              text: "Потеряно соединение с сервером\nПерезайдите в приложение или попробуйте позже",
              timestamp: Date.now() + 5 * 1000,
              type: "error",
            },
          ],
        });
        setTimeout(() => {
          setTriggerAuth((bool) => !bool);
        }, 1500);
      }
    } else {
      useAlertStore.setState({
        alertArray: [],
      });
    }
  }

  // 🔁 Обработка состояния авторизации
  useEffect(() => {
    if (auth.isLoading) return;

    const now = Date.now();
    if (now - lastExecutedReference.current < 2000) {
      return; // Защита от слишком частого выполнения
    }

    lastExecutedReference.current = now;

    const token = useAppStore.getState().token;
    if (token && !isValidToken(token)) {
      // console.log(token);

      refreshToken().then(({ success }) => {
        if (!success) {
          if (!isObjectEmpty(useAppStore.getState()?.message)) {
            useAppStore.setState({
              cacheMessage: useAppStore.getState()?.message,
            });
          }
          auth.signinRedirect();
        }
      });
    } else if (auth.user === null) {
      console.log("no user found");
      if (!isObjectEmpty(useAppStore.getState()?.message)) {
        useAppStore.setState({
          cacheMessage: useAppStore.getState()?.message,
        });
      }
      auth.signinRedirect();
    } else if (auth?.user) {
      updateTokenData(auth?.user);
    }
  }, [auth.isAuthenticated, auth.user, token, auth.error, triggerAuth]);
}

export default TokenUpdater;
